import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

/**
 * PolicyAcknowledgmentModal — one-time soft acknowledgment.
 *
 * Fires on first logged-in page view when the user's stored
 * acknowledgment version doesn't match the current POLICY_VERSION
 * returned by `/api/policies/version`.
 *
 * Three required checkboxes:
 *   1. Terms of Use
 *   2. Privacy Policy
 *   3. Beta Confidentiality (prominent — this is why the modal exists)
 *
 * "I agree" is enabled only when all three are checked.
 * "Cancel" (soft decline) calls onDecline() — the parent signs the
 * user out and shows a toast.
 *
 * The modal is not a hard gate on the API; it's a per-user record of
 * acknowledgment. The frontend decides when to show it.
 */
const PolicyAcknowledgmentModal = ({ onAgree, onDecline, submitting = false }) => {
  const [ackTerms, setAckTerms] = useState(false);
  const [ackPrivacy, setAckPrivacy] = useState(false);
  const [ackConfidentiality, setAckConfidentiality] = useState(false);

  const allChecked = ackTerms && ackPrivacy && ackConfidentiality;

  const handleAgree = () => {
    if (!allChecked || submitting) return;
    onAgree({
      terms: true,
      privacy: true,
      beta_confidentiality: true,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 py-6 overflow-y-auto"
      data-testid="policy-ack-modal"
    >
      <div className="w-full max-w-xl bg-stone-950/95 border border-white/10 rounded-sm shadow-2xl p-8">
        <h2
          className="text-3xl font-light text-white mb-2"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
          data-testid="policy-ack-heading"
        >
          A few words before we begin
        </h2>
        <p className="text-sm text-zinc-400 leading-relaxed mb-6">
          SAGE is in <em>private beta</em>. Before continuing, please take a
          moment with the three agreements below. You can revisit the full
          documents any time from the footer.
        </p>

        {/* Beta confidentiality — elevated, boxed callout. */}
        <div
          className="border border-amber-500/20 bg-amber-500/5 rounded-sm p-4 mb-5"
          data-testid="policy-ack-confidentiality-callout"
        >
          <p className="text-xs font-mono uppercase tracking-widest text-amber-300/80 mb-2">
            Beta Confidentiality
          </p>
          <p className="text-sm text-zinc-300 leading-relaxed">
            You're one of the first people inside SAGE. Please don't publicly
            post screen recordings, screenshots, system prompts, or
            proprietary details of the product while it's in beta. Talk about
            what SAGE <em>does</em> freely — just keep the inside of the
            product private. Full details are in the Terms.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={ackTerms}
              onChange={(e) => setAckTerms(e.target.checked)}
              data-testid="policy-ack-terms-checkbox"
              className="mt-1 h-4 w-4 accent-amber-400 cursor-pointer"
            />
            <span className="text-sm text-zinc-300 leading-relaxed">
              I've read the{' '}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-zinc-100 hover:text-white"
                data-testid="policy-ack-terms-link"
              >
                Terms of Use
              </Link>
              {' '}and agree to them.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={ackPrivacy}
              onChange={(e) => setAckPrivacy(e.target.checked)}
              data-testid="policy-ack-privacy-checkbox"
              className="mt-1 h-4 w-4 accent-amber-400 cursor-pointer"
            />
            <span className="text-sm text-zinc-300 leading-relaxed">
              I've read the{' '}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-zinc-100 hover:text-white"
                data-testid="policy-ack-privacy-link"
              >
                Privacy Policy
              </Link>
              {' '}and agree to it.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={ackConfidentiality}
              onChange={(e) => setAckConfidentiality(e.target.checked)}
              data-testid="policy-ack-confidentiality-checkbox"
              className="mt-1 h-4 w-4 accent-amber-400 cursor-pointer"
            />
            <span className="text-sm text-zinc-300 leading-relaxed">
              I agree to keep SAGE's beta private — no public screen
              recordings, scraping, or sharing proprietary details.
            </span>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            disabled={!allChecked || submitting}
            onClick={handleAgree}
            data-testid="policy-ack-agree-button"
            className="flex-1 threshold-button rounded-sm py-2"
          >
            {submitting ? 'Saving…' : 'I agree'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onDecline}
            disabled={submitting}
            data-testid="policy-ack-cancel-button"
            className="text-zinc-400 hover:text-zinc-200"
          >
            Cancel
          </Button>
        </div>

        <p className="text-xs text-zinc-600 mt-5 leading-relaxed">
          Cancel will sign you out. You can return any time from the invite
          link.
        </p>
      </div>
    </div>
  );
};

export default PolicyAcknowledgmentModal;
